import React from 'react';
import { motion } from 'framer-motion';

const SkeletonLoader = ({ className = '', variant = 'pulse' }) => {
  if (variant === 'shimmer') {
    return (
      <div className={`relative overflow-hidden bg-gray-200 rounded ${className}`}>
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
          animate={{
            x: ['-100%', '100%']
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </div>
    );
  }

  if (variant === 'wave') {
    return (
      <motion.div
        className={`bg-gray-200 rounded ${className}`}
        animate={{
          opacity: [0.5, 1, 0.5]
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
    );
  }

  // Default: pulse
  return (
    <div className={`bg-gray-200 rounded animate-pulse ${className}`} />
  );
};

// Preset skeleton components
export const SkeletonCard = ({ variant = 'shimmer' }) => (
  <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-100 p-4 sm:p-6 space-y-4">
    <div className="flex items-start gap-3">
      <SkeletonLoader variant={variant} className="w-12 h-12 flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <SkeletonLoader variant={variant} className="h-6 w-3/4" />
        <SkeletonLoader variant={variant} className="h-4 w-1/2" />
      </div>
    </div>
    <div className="space-y-3">
      <SkeletonLoader variant={variant} className="h-4 w-full" />
      <SkeletonLoader variant={variant} className="h-4 w-full" />
      <SkeletonLoader variant={variant} className="h-4 w-2/3" />
    </div>
  </div>
);

export const SkeletonTable = ({ rows = 5, variant = 'shimmer' }) => (
  <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
    <div className="p-4 border-b border-gray-100">
      <SkeletonLoader variant={variant} className="h-6 w-48" />
    </div>
    <div className="divide-y divide-gray-100">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="p-4 flex items-center gap-4">
          <SkeletonLoader variant={variant} className="w-10 h-10 rounded-full" />
          <div className="flex-1 space-y-2">
            <SkeletonLoader variant={variant} className="h-4 w-1/3" />
            <SkeletonLoader variant={variant} className="h-3 w-1/2" />
          </div>
          <SkeletonLoader variant={variant} className="h-8 w-20" />
        </div>
      ))}
    </div>
  </div>
);

export const SkeletonFormGrid = ({ count = 3, variant = 'shimmer' }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonCard key={i} variant={variant} />
    ))}
  </div>
);

// MyForms page specific skeleton loader
export const SkeletonMyForms = ({ variant = 'shimmer' }) => (
  <div className="space-y-4 sm:space-y-6 lg:space-y-8 p-3 sm:p-4 lg:p-6">
    {/* Header */}
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
      <div className="min-w-0 px-2 sm:px-0 flex-1">
        <SkeletonLoader variant={variant} className="h-8 sm:h-10 lg:h-12 w-48 sm:w-64 mb-2" />
        <SkeletonLoader variant={variant} className="h-4 sm:h-5 lg:h-6 w-64 sm:w-80" />
      </div>
      <SkeletonLoader variant={variant} className="h-10 sm:h-12 w-full sm:w-48 rounded-lg sm:rounded-xl flex-shrink-0" />
    </div>

    {/* Search and Filter Section */}
    <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100">
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
        <SkeletonLoader variant={variant} className="h-11 sm:h-12 flex-1 rounded-lg sm:rounded-xl" />
        <SkeletonLoader variant={variant} className="h-11 sm:h-12 w-full sm:w-48 rounded-lg sm:rounded-xl" />
      </div>
    </div>

    {/* Form Cards Grid */}
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="bg-white rounded-xl sm:rounded-2xl border border-gray-100 p-4 sm:p-6">
          {/* Card Header */}
          <div className="flex items-start justify-between mb-4 sm:mb-6 gap-2">
            <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
              <SkeletonLoader variant={variant} className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg flex-shrink-0" />
              <div className="flex-1 min-w-0 space-y-2">
                <SkeletonLoader variant={variant} className="h-5 sm:h-6 w-3/4" />
                <SkeletonLoader variant={variant} className="h-5 w-20 rounded-full" />
              </div>
            </div>
            <SkeletonLoader variant={variant} className="w-8 h-8 rounded-lg flex-shrink-0" />
          </div>

          {/* Stats */}
          <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
            {Array.from({ length: 3 }).map((_, j) => (
              <div key={j} className="flex items-center justify-between">
                <SkeletonLoader variant={variant} className="h-4 w-24" />
                <SkeletonLoader variant={variant} className="h-4 w-12" />
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="pt-3 sm:pt-4 border-t border-gray-100">
            <SkeletonLoader variant={variant} className="h-3 w-32" />
          </div>
        </div>
      ))}
    </div>

    {/* Quick Stats */}
    <div className="bg-blue-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="text-center md:text-left">
            <SkeletonLoader variant={variant} className="h-4 w-24 mb-2 mx-auto md:mx-0 bg-blue-400/30" />
            <SkeletonLoader variant={variant} className="h-8 sm:h-10 lg:h-12 w-16 mx-auto md:mx-0 bg-blue-400/30" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default SkeletonLoader;
